<template>
  <section>
    <question v-if="display"></question>
    <errors v-if="isVisibleErrorsModal"></errors>
    <accountCreation v-if="isAccountCreationModalVisible"></accountCreation>
    <address-suggestion v-if="isSuggestedAddressModalVisible"></address-suggestion>
    <div class="content-wrapper">

      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br />

      <div class="row">
        <spinner v-if="isLoading"></spinner>
        <div class="col-lg-12">

          <div id="panelChart9" class="panel panel-default">
            <div v-if="$route.query.retry" class="alert alert-warning" role="alert">
              <h4><em class="icon-exclamation pr-sm"></em>There was an issue validating your personal information.</h4>
              <p>Please verify your information and submit it once more.</p>
            </div>

            <div class="panel-title">
              User information
            </div>
            <div class="line-separator"></div>

            <userTypeSelection-form v-if="!isLoading"
            v-bind:user="user"
            ref="basicFinancialInformation"></userTypeSelection-form>

            <div class="line-separator"></div>

            <div class="row">
              <div class="col-md-3">
                <div>
                  <label>Comment Category</label>
                </div>
              </div>
            </div>

            <div class="row bottom-margin">
              <div class="col-md-4">
                <div>
                  <multiselect 
                    v-model="userCommentCategory" 
                    :options="userCommentCategories"
                    :showToggleAll=false
                    placeholder="Select relevant categories"
                    optionLabel="name" 
                  />
                </div>
              </div>
            </div>

            <div class="row bottom-margin">
              <div class="col-md-12">
                <span>Please append your comment below</span>
                <span v-if="savingComment" class="auto-save-notice-label auto-save-notice-saving-color">[ Saving... ]</span>
                <span v-if="commentSaved" class="auto-save-notice-label auto-save-notice-saved-color">[ Saved ]</span>
                <span v-if="commentSaveError" class="auto-save-notice-label auto-save-notice-error-color">[ Not Saved ]</span>
                <restricted-textarea
                  :limit="255"
                  :input="userCommentInput"
                  id="comment"
                  :required="false"
                  :showNotice="false"
                  :showLimit="true"
                  :textColorClass="'vinous-text'"
                  placeholder=""
                  @inputUpdated="commentUpdated"
                  class="restricted-textarea"
                />
              </div>
            </div>

           <basicFinancial-information v-if="!isLoading"
              v-bind:basicFinancialInformation="user"
              @validated="basicInformationValidated"
              @emailUpdated="handleEmailChange"
              ref="basicFinancialInformation"></basicFinancial-information>

          <div class="checkbox c-checkbox check-margin">
            <label class="dark-grey-text">
              <input type="checkbox" value="" name="provide-investor-login" v-model="provideInvestorLogin" :checked="shouldBeCheckedByDefault" :disabled="!emailIsValid">
              <span class="fa fa-check"></span>
              Provide investor log in
            </label>
          </div>

          <div class="checkbox c-checkbox check-margin">
            <label class="dark-grey-text">
              <input type="checkbox" value="" name="enable-mfa" v-model="enableMfa" :disabled="!provideInvestorLogin">
              <span class="fa fa-check"></span>
              Enable MFA
            </label>
          </div>

          <addresses-form
            v-if="user.residentialAddress && user.mailingAddress"
            v-bind:firstTitle="'Business address'"
            v-bind:residentialAddress="user.residentialAddress"
            v-bind:secondTitle="'Mailing address'"
            v-bind:mailingAddress="user.mailingAddress"
            v-bind:key="1"
            v-bind:is-admin="true"
            @validated="addressesValidated"
            ref="addresses"
            ></addresses-form>

            <div class="row top-margin bottom-margin">
              <div class="col-md-2">
                <button type="submit" v-bind:disabled="disableSubmitButton()" class="btn btn-info btn-lg button-submit" v-on:click="validateAndSubmitUser()">Submit</button>
              </div>
            </div>

            <dismissable-alert
              title="You've changed the default account"
              subtitle=""
              :closeAction="hideDefaultAccountAlert"
              :isShowing="showDefaultAccountChanged"
            />
            <AccountsList
                @confirmedChangeDefaultAccount="changedDefaultAccountId"
                v-if="user !== undefined"
                :userType="user.userType"
                :defaultAccountId="user.defaultAccountId"
                :accounts="user.accounts" />

            <div class="line-separator top-margin"></div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script>
  import {
    mapMutations,
    mapActions,
    mapState,
  } from 'vuex';

  import orderBy from 'lodash/orderBy';
  import debounce from 'lodash/debounce';

  import {
    SET_ERRORS_MODAL_INFO,
    SET_SUGGESTED_ADDRESS,
    SET_USER_BY_ADMIN,
    SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
    SET_SUGGESTED_ADDRESS_TYPE,
    SET_SUGGESTED_ADDRESS_YES_METHOD,
    SHOW_ERRORS_MODAL,
    SHOW_INFORMATION_MODAL,
    SHOW_QUESTION_MODAL,
    HIDE_QUESTION_MODAL,
  } from '@/store/mutation-types';

  import MultiSelect from 'primevue/multiselect';
  import datePicker from 'vue-bootstrap-datetimepicker';
  import addressSuggestion from '@/components/modals/AddressSuggestion';
  import errors from '@/components/modals/Errors';
  import information from '@/components/modals/Information';
  import spinner from '@/components/widgets/Spinner';
  import Question from '@/components/modals/Question';
  import accountCreation from '@/components/modals/AccountCreation';
  import addresses from '@/components/Addresses';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import basicFinancialInformation from '@/components/BasicFinancialInformation';
  import userTypeSelection from '@/components/UserTypeSelection';
  import AccountsList from '@/components/AccountsList';
  import breadcrumb from '@/components/Breadcrumb';
  import RestrictedTextArea from '@/components/formControls/RestrictedTextArea';

  import {
    formatDate,
  } from '@/util';

  import {
    Constants,
  } from '@/data/static/constants';

  const mix = {
    methods: {
      formatDate,
    },
  };

  export default {
    name: 'EditFinancialAdvisor',
    beforeMount() {
      this.initializeState();
      this.loadUserDetail();
    },
    watch: {
      async suggestedAddressModalYesCall({ payload, suggestedAddress, addressType }) {
        if (!this.$refs.addresses.isMailingAddressDifferent) {
          this.user.residentialAddress = suggestedAddress;
          this.user.mailingAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        if (['residential', 'organization'].includes(addressType)) {
          this.user.residentialAddress = suggestedAddress;
          this.$refs.addresses.setResidencialAddress(suggestedAddress);
        } else if (addressType === 'mailing') {
          this.user.mailingAddress = suggestedAddress;
          this.$refs.addresses.setMailingAddress(suggestedAddress);
        }
        await this.save(payload);
      },
      userCommentCategory() {
        if (!this.userCommentCategoriesHasChanged) return;
        this.saveComments();
      },
      provideInvestorLogin(provideInvestorLogin) {
        if (!provideInvestorLogin) {
          this.enableMfa = false;
        }
      },
      user: function changedUser(newUser) {
        this.breadcrumbData = [];
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          }),
        );
        const title = newUser.firstName.concat(' ', newUser.lastName);
        const goto = { name: this.$route.name, params: Object.assign({}, this.$route.params) };
        this.breadcrumbData.push(
          Object.assign({}, {
            title,
            goto,
            selected: true,
          }),
        );
      },
    },
    mixins: [mix],
    data() {
      return {
        userId: '',
        minimumAge: 18,
        breadcrumbData: [],
        user: {},
        isLoading: true,
        showDefaultAccountChanged: false,
        isBasicInformationValid: true,
        creationSucceeded: false,
        isValidAddress: true,
        provideInvestorLogin: false,
        enableMfa: false,
        emailIsValid: false,
        userCommentCategory: [],
        userCommentInput: '',
        savingComment: false,
        commentSaved: false,
        commentSaveError: false,
      };
    },
    computed: {
      ...mapState('app', [
        'accountTypes',
        'userCommentTypes',
      ]),
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('accountCreation', [
        'isSuggestedAddressModalVisible',
        'isAccountCreationModalVisible',
        'changeAddress',
        'suggestedAddressModalYesCall',
      ]),
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('question', [
        'display',
      ]),
      ...mapState('app', [
        'userCommentCategories',
      ]),
      userCommentCategoriesHasChanged() {
        return this.user.commentCategories !== this.userCommentCategory;
      },
      userCommentsHasChanged() {
        return this.user.comments !== this.userCommentInput;
      },
      shouldBeCheckedByDefault() {
        return this.provideInvestorLogin || this.user.clientId !== null;
      },
    },
    methods: {
      setSavingComment() {
        this.savingComment = true;
        this.commentSaved = false;
        this.commentSaveError = false;
      },
      setCommentSaved() {
        this.savingComment = false;
        this.commentSaved = true;
        this.commentSaveError = false;
      },
      setCommentSaveError() {
        this.savingComment = false;
        this.commentSaved = false;
        this.commentSaveError = true;
      },
      saveComments() {
        this.setSavingComment();

        const payload = {
          comments: this.userCommentInput,
          commentCategories: this.userCommentCategory.map(c => c.id),
        };
        this.addUserComments({ userId: this.user.id, payload })
        .then(() => {
          this.user = Object.assign(
            this.user, { comments: this.userCommentInput },
          );
          this.setCommentSaved();
        })
        .catch(() => {
          this.setCommentSaveError();
        });
      },
      debouncedSave: debounce(function fn() {
        this.saveComments();
      }, 800),
      commentUpdated($event) {
        this.setSavingComment();
        this.userCommentInput = $event;
        if (!this.userCommentsHasChanged) return;

        this.debouncedSave();
      },
      hideDefaultAccountAlert() {
        this.showDefaultAccountChanged = false;
      },
      handleEmailChange($event) {
        this.emailIsValid = $event.valid;
        if (!this.emailIsValid) {
          this.provideInvestorLogin = false;
        }
      },
      ...mapActions('user', [
        'updateFinancialAdvisor',
        'addUserComments',
      ]),
      ...mapActions('accountCreation', [
        'setDefaultAccount',
        'getUserDetail',
        'verifyOrganizationAddress',
        'verifyMailingAddress',
      ]),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      ...mapMutations('accountCreation', {
        setSuggestedAddress: SET_SUGGESTED_ADDRESS,
        setUserByAdmin: SET_USER_BY_ADMIN,
        setSuggestedAddressType: SET_SUGGESTED_ADDRESS_TYPE,
        setSuggestedAddressModalVisible: SET_SUGGESTED_ADDRESS_MODAL_VISIBLE,
        setSuggestedAddressYesMethod: SET_SUGGESTED_ADDRESS_YES_METHOD,
      }),
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      initializeState() {
        this.userId = this.$route.params.userId;
        this.name = this.$route.params.name;
      },
      async loadUserDetail() {
        this.isLoading = true;
        await this.getUserDetail(this.userId).then((data) => {
          this.user = Object.assign({}, {
            ...data,
            commentCategories: (data.commentCategories || []).map(
              c => ({
                id: c.commentCategory.id,
                name: c.commentCategory.name,
              }),
            ),
          });
          this.sortAccounts();
          this.userCommentCategory = this.user.commentCategories;
          this.userCommentInput = data.comments;
          this.provideInvestorLogin = Boolean(data.clientId);
          this.enableMfa = data.isMfaEnabled;
          this.isLoading = false;
        }).catch(() => {
          this.isLoading = false;
        });
      },
      sortAccounts() {
        this.user.accounts = orderBy(this.user.accounts, [(account) => {
          const name = account.accountName;
          return name ? account.accountName.toLowerCase() : '';
        }], ['asc']);
      },
      basicInformationValidated(isValid) {
        this.isBasicInformationValid = isValid;
      },
      addressesValidated(isValid) {
        this.isValidAddress = isValid;
      },
      updateAddress(originalAddress, updatedAddress) { /* eslint-disable no-param-reassign */
        originalAddress.streetAddress1 = updatedAddress.streetAddress1;
        originalAddress.streetAddress2 = updatedAddress.streetAddress2;
        originalAddress.zipCode = updatedAddress.zipCode;
        originalAddress.state = updatedAddress.state;
        originalAddress.city = updatedAddress.city;
      },
      createPayload() {
        const basicInformation = this.$refs.basicFinancialInformation.getData();
        const {
          alternateCompanyName,
          CRDnumber,
          email,
          firstName,
          lastName,
          organizationName,
          primaryPhoneNum,
          secondaryPhoneNum,
        } = basicInformation;

        const addressesInformation = this.$refs.addresses.getData();

        const {
          mailingAddress,
          residentialAddress,
        } = addressesInformation;

        return {
          email,
          firstName,
          lastName,
          mailingAddress,
          ...(
            this.user.userType === 'ADVISOR' ?
            { organizationAddress: residentialAddress } :
            { residentialAddress }
          ),
          primaryPhoneNum,
          secondaryPhoneNum,
          alternateCompanyName,
          CRDnumber,
          organizationName,
        };
      },
      validateAndSubmitUser() {
        this.isLoading = true;
        this.setUserByAdmin(this.user);
        const payload = this.createPayload();

        this.user.residentialAddress = Object.assign(
          this.user.residentialAddress,
          payload.residentialAddress);

        this.user.mailingAddress = Object.assign(
          this.user.mailingAddress,
          payload.mailingAddress);

        const account = this.user.accounts
          .find(item => item.accountType === this.accountTypes.ADVISOR.name);

        payload.accountId = account.id;
        payload.userType = this.user.userType;

        return this.showConfirmationModal(payload);
      },
      showUserModalsError(listUserErrors) {
        this.setErrorsModalInfo(listUserErrors);
        this.showErrorsModal();
      },
      disableSubmitButton() {
        if (this.$refs.addresses) {
          this.isValidAddress = this.$refs.addresses.validate();
        }
        return !this.isBasicInformationValid || !this.isValidAddress
          || this.isLoading;
      },
      showConfirmationModal(payload) {
        this.isLoading = false;
        this.showQuestionModal({
          title: Constants.userDetail.modalTitle,
          question: Constants.userDetail.modalQuestion,
          method: async () => {
            await this.methodToExecuteInQuestionModal(payload);
          },
          yesButtonText: Constants.userDetail.modalYesButtonText,
          hideCancelButton: false,
        });
      },
      async changedDefaultAccountId(data) {
        this.isLoading = true;
        try {
          await this.setDefaultAccount(data);
          await this.loadUserDetail();
          this.isLoading = false;
          this.showDefaultAccountChanged = true;
        } catch (error) {
          this.isLoading = false;
        }
      },
      async save(userData) {
        this.isLoading = true;
        try {
          const addressData = this.$refs.addresses.getData();

          // Advisors address -> organization
          const address = this.user.userType === 'ADVISOR'
            ? { organizationAddress: addressData.residentialAddress }
            : { residentialAddress: addressData.residentialAddress };

          const payload = {
            ...userData,
            ...address,
            mailingAddress: addressData.mailingAddress,
          };

          await this.updateFinancialAdvisor({
            payload,
            userId: this.user.id,
            provideLogin: this.provideInvestorLogin,
            enableMfa: this.enableMfa,
          });

          this.isLoading = false;

          await this.loadUserDetail();
        } catch (err) {
          this.isLoading = false;
        }
      },
      async methodToExecuteInQuestionModal(payload) {
        this.hideQuestionModal();
        this.isLoading = true;
        return this.save(payload);
      },
    },
    components: {
      datePicker,
      errors,
      spinner,
      information,
      accountCreation,
      AccountsList,
      breadcrumb,
      question: Question,
      multiselect: MultiSelect,
      'address-suggestion': addressSuggestion,
      'addresses-form': addresses,
      'dismissable-alert': DismissableAlert,
      'basicFinancial-information': basicFinancialInformation,
      'userTypeSelection-form': userTypeSelection,
      'restricted-textarea': RestrictedTextArea,
    },
  };
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
  .auto-save-notice-label {
    margin-left: 6px;
    font-weight: bold;
    font-size: 12px;
  }

  .auto-save-notice-saving-color {
    color: rgb(19, 134, 151);
  }

  .auto-save-notice-saved-color {
    color: rgb(13, 149, 22);
  }

  .auto-save-notice-error-color {
    color: rgb(196, 62, 0);
  }

  .restricted-textarea {
    height: 90px !important;
  }

  .message-feedback {
    background-color: #dbe9db;
    margin-left: 2%;
    margin-top: 2%;
    margin-bottom: 2%;
    width: 66%;
  }

  .birth-field-bottom {
    margin-bottom: 5px;
  }

  .button-submit {
    background-color: #127285;
  }

  .date-pick-style {
    width: 90%;
  }

  .content-wrapper {
    border-top: none;
  }

  .warning-heading {
    background-color: #f9f2ce !important;
    padding: 10px;
    margin: 0 auto 20px;
    font-size: 14px;
    font-weight: 500;
  }

  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    padding-left: 20px;
  }

  .icon-back {
    position: relative;
  }

  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position: absolute;
    left: 0px;
  }

  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }

  .left-margin {
    margin-left: 4%;
  }

  .right-margin {
    margin-right: 4%;
  }

  .top-margin {
    margin-top: 2%;
  }

  .bottom-margin {
    margin-bottom: 2%;
  }

  .space {
    margin-top: 4%;
  }

  .content-heading {
    font-weight: normal;
  }

  .upload-container {
    height: 80px;
    padding-top: 10px;
  }

  .panel-title {
    font-size: 20px;
  }

  .panel-subtitle {
    font-size: 16px;
  }

  .panel-default {
    padding: 30px 40px;
  }

  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }

  label {
    color: #888;
    font-size: 14px;
    width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 0px;
  }

  select:required:invalid {
    color: lightgray;
  }

  option[value=""][disabled] {
    display: none;
  }

  option {
    color: black;
  }

   ::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: lightgray;
    opacity: 1;
    /* Firefox */
  }

   :-ms-input-placeholder {
    /* Internet Explorer 10-11 */
    color: lightgray;
  }

   ::-ms-input-placeholder {
    /* Microsoft Edge */
    color: lightgray;
  }

  .disabled-state {
    color: #ccc;
  }

  .text-red {
    color: #a94442;
  }

  .btn-new {
    margin-right: 8px;
  }

  .table-fixed-header--sm {
    height: 240px;
  }

  .table-fixed-header--sm tbody {
    height: 176px;
  }

</style>



// WEBPACK FOOTER //
// EditFinancialAdvisor.vue?2d165ee2